import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, money } from "@/lib/api";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState(result === "success" ? "checking" : "cancel");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (result !== "success" || !sid) { return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "expired") { setState("expired"); return; }
      } catch (e) { /* retry */ }
      if (++tries < 15) timer = setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [result, sid]);

  return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="payment-result">
      {state === "checking" && <><Loader2 className="w-14 h-14 animate-spin text-[#0096C7] mx-auto" /><p className="mt-4 font-semibold" data-testid="payment-checking">Confirming your gift…</p></>}
      {state === "paid" && <>
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
        <h1 className="font-display uppercase text-4xl text-[#03045E] mt-4" data-testid="payment-success-title">Thank you!</h1>
        <p className="mt-3 text-slate-600">Your {money(info?.amount)} gift for <b>{info?.category_label}</b> is confirmed. Every step helps fund the next child and the next pond.</p>
        <div className="mt-8 text-left rounded-2xl bg-white border border-sky-100 p-6 shadow-sm" data-testid="donation-receipt">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0096C7]">Contribution acknowledgment</p>
          <dl className="grid grid-cols-[110px_1fr] gap-y-1 text-sm mt-3">
            <dt className="font-bold">Organization</dt><dd>Swim for Rakim™ — Life After The Storm Ministries Inc</dd>
            <dt className="font-bold">Amount</dt><dd data-testid="receipt-amount">{money(info?.amount)}</dd>
            <dt className="font-bold">Date</dt><dd>{new Date().toLocaleDateString()}</dd>
            <dt className="font-bold">Designation</dt><dd>{info?.category_label}{info?.project_code ? ` — ${info.project_code}` : ""}</dd>
            <dt className="font-bold">Reference</dt><dd className="break-all">{sid?.slice(-12)}</dd>
          </dl>
          <p className="text-sm text-slate-700 mt-4" data-testid="receipt-wording">Thank you for your charitable contribution to Swim for Rakim™. No goods or services were provided in exchange for this contribution, except for intangible religious benefits, if applicable. Please retain this acknowledgment for your records. Contributions are deductible to the extent permitted by law.</p>
          <p className="text-xs text-slate-500 mt-3">Swim for Rakim™ is an auxiliary ministry of Life After The Storm Ministries Inc, a 508(c)(1)(A) faith-based ministry.</p>
          <button onClick={() => window.print()} className="no-print mt-4 rounded-full border border-sky-300 px-5 py-2 text-sm font-bold text-[#0077B6]" data-testid="receipt-print-button">Print / save receipt</button>
        </div>
        {info?.project_code && <Link to={`/projects/${info.project_code}`} className="inline-block mt-6 font-bold text-[#0077B6]" data-testid="payment-project-link">See {info.project_code} →</Link>}
      </>}
      {["cancel", "expired", "timeout"].includes(state) && <>
        <XCircle className="w-16 h-16 text-slate-400 mx-auto" />
        <h1 className="font-display uppercase text-3xl text-[#03045E] mt-4" data-testid="payment-cancel-title">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
        <p className="mt-3 text-slate-600">{state === "timeout" ? "We couldn't confirm yet — please check back shortly." : "No charge was made. You can try again anytime."}</p>
      </>}
      <Link to="/sponsor" className="inline-block mt-8 rounded-full bg-[#03045E] text-white px-6 py-3 font-bold uppercase" data-testid="payment-back-button">Back to the marketplace</Link>
    </div>
  );
}
